import {CarePlanConfigurationFeature} from '../../../../../backend/enums/care-plan-configuration-feature';
import {CarePlanConfigurationItemAutocompleteItem} from '../../../../../backend/interfaces/care-plan-configuration-item-autocomplete-item';
import {EntityActivitiesRepository} from '../../../../../repositories/entity-activities';
import {
  CLINICAL_SUPPORT__MEDICATION_ID,
  CLINICAL_SUPPORT__MEDICATION__MEDICATE_ID,
  PERSONAL_DEVELOPMENT__SOCIAL_INTERACTION_ID,
} from '../../../../constants/hardcoded';
import {BaseReportData} from './export-care-plan-get-report-data';
import {ExportActivityTypeData} from './export-care-plan-types';

/**
 * Get additional needed data for the report for specific edge cases (eg: Medication)
 */
export async function processAdditionalActivityTypeData(
  baseData: BaseReportData,
  exportActivityTypesData: ExportActivityTypeData[]
) {
  switch (baseData.groupData.id) {
    // --- MEDICATION EDGE CASE ---
    case CLINICAL_SUPPORT__MEDICATION_ID:
      await MedicationAdditionalData(baseData, exportActivityTypesData);
      break;

    // --- SOCIAL INTERACTION EDGE CASE ---
    case PERSONAL_DEVELOPMENT__SOCIAL_INTERACTION_ID:
      await SocialInteractionAdditionalData(baseData, exportActivityTypesData);
      break;

    default:
      break;
  }
}

// --------------------------------------------------------------------------
// --------------------------------------------------------------------------
// --------------------------------------------------------------------------

async function MedicationAdditionalData(
  baseData: BaseReportData,
  exportActivityTypesData: ExportActivityTypeData[]
) {
  const entityId = baseData.entityData.id;
  const activitiesSectorId = baseData.sectorData.id as string;
  const activitiesGroupId = baseData.groupData.id;
  let cache: Record<string, CarePlanConfigurationItemAutocompleteItem> = {};

  for (const exportActivityType of exportActivityTypesData) {
    // Medication edge case: aditional data
    if (exportActivityType.activityType.id === CLINICAL_SUPPORT__MEDICATION__MEDICATE_ID) {
      const medicineAdministrationRouteConfig = exportActivityType.config.find(
        (c) => c.feature === CarePlanConfigurationFeature.MedicineAdministrationRoute
      );
      const medicineAdministrationRouteValue = exportActivityType.configValues?.find(
        (v) => v.carePlanConfigurationId === medicineAdministrationRouteConfig?.id
      );

      let data: CarePlanConfigurationItemAutocompleteItem;

      // 1. medicine-administration-route value
      if (
        medicineAdministrationRouteConfig?.id &&
        medicineAdministrationRouteValue?.carePlanConfigurationValue
      ) {
        if (cache[medicineAdministrationRouteValue.carePlanConfigurationValue]) {
          data = cache[medicineAdministrationRouteValue.carePlanConfigurationValue];
        } else {
          data = await EntityActivitiesRepository.instance.getGroupCarePlanConfigurationDataValue(
            entityId,
            activitiesSectorId,
            activitiesGroupId,
            exportActivityType.activityType.id,
            medicineAdministrationRouteConfig.id,
            medicineAdministrationRouteValue.carePlanConfigurationValue
          );

          cache[medicineAdministrationRouteValue.carePlanConfigurationValue] = data;
        }

        medicineAdministrationRouteValue.carePlanConfigurationValue = data.name;
      }

      // 2. medicine value
      const medicineConfig = exportActivityType.config.find(
        (c) => c.feature === CarePlanConfigurationFeature.Medicine
      );
      const medicineConfigValue = exportActivityType.configValues?.find(
        (v) => v.carePlanConfigurationId === medicineConfig?.id
      );

      if (medicineConfig?.id && medicineConfigValue?.carePlanConfigurationValue) {
        if (cache[medicineConfigValue.carePlanConfigurationValue]) {
          data = cache[medicineConfigValue.carePlanConfigurationValue];
        } else {
          data = await EntityActivitiesRepository.instance.getGroupCarePlanConfigurationDataValue(
            entityId,
            activitiesSectorId,
            activitiesGroupId,
            exportActivityType.activityType.id,
            medicineConfig.id,
            medicineConfigValue.carePlanConfigurationValue
          );

          cache[medicineConfigValue.carePlanConfigurationValue] = data;
        }

        medicineConfigValue.carePlanConfigurationValue = data.name;
      }
    }
  }
}

async function SocialInteractionAdditionalData(
  baseData: BaseReportData,
  exportActivityTypesData: ExportActivityTypeData[]
) {
  const entityId = baseData.entityData.id;
  const activitiesSectorId = baseData.sectorData.id as string;
  const activitiesGroupId = baseData.groupData.id;
  const seniorId = baseData.userCarePlanData.userId;

  for (const exportActivityType of exportActivityTypesData) {
    const contactsConfig = exportActivityType.config.find(
      (c) => c.feature === CarePlanConfigurationFeature.UserContacts
    );
    const contactsConfigValue = exportActivityType.configValues?.find(
      (v) => v.carePlanConfigurationId === contactsConfig?.id
    );

    if (contactsConfig?.id && contactsConfigValue?.carePlanConfigurationValue) {
      // Fetch contact name.
      const contactUserId = contactsConfigValue.carePlanConfigurationValue as string;
      const activityTypeId = exportActivityType.activityType.id as string;
      const contacts =
        // TODO: fetch all not only the first 20.
        await EntityActivitiesRepository.instance.getCareplanConfigurationOptionItems(
          entityId,
          activitiesSectorId,
          activitiesGroupId,
          activityTypeId,
          contactsConfigValue.carePlanConfigurationId,
          0,
          20,
          seniorId
        );

      const contactName = contacts.data.find((c) => c.id === contactUserId)?.name || 'n/a';

      // Update config value with contact name
      contactsConfigValue.carePlanConfigurationValue = contactName;
    }
  }
}
